import { createSlice } from '@reduxjs/toolkit';
import { DynamoConnector } from '../../DynamoConnector';

/**
 * Upgrades slice — backs the deck Upgrade Finder.
 *
 * The store holds two distinct query surfaces:
 *
 * 1. **Legacy format-wide category search** (`list` / `nextCursor`) used by
 *    the "By Role" mode. Drives `/upgrades?id=…&category=…`. Only
 *    `fetchUpgradesListByCategory` writes here.
 *
 * 2. **Source-based commander-tuned queries** (`items` / `cursor` / `status`
 *    / `source` / …) used by every other mode (staples, synergy_*,
 *    manabase, archetype, category_role, combos, top_synergies). Drives
 *    `/upgrades?id=…&source=<enum>…`. Only `fetchUpgrades` writes here.
 *
 * The two surfaces are kept separate so mode switching doesn't clobber the
 * legacy list and vice versa — the pane component reads whichever one
 * matches the currently selected mode.
 */

const initialState = {
    // Legacy (format-wide category) state.
    categories: [],
    list: [],
    isFetching: false,
    loadingState: 'idle',
    nextCursor: null,
    selectedListKey: null,
    selectedListIndex: -1,
    forceSkipAutoLoad: false,
    hasDoneFirstLoad: false,

    // Source-based commander-tuned state.
    items: [],
    cursor: null,
    total: 0,
    status: 'idle',
    binKey: null,
    deckCount: 0,
    source: null,
    note: null,
    isFetchingSource: false,
    sourceLoadingState: 'idle',
    hasDoneFirstSourceLoad: false,
    error: null,
};

export const upgradesSlice = createSlice({
    name: 'upgrades',
    initialState,
    reducers: {
        emptyUpgradesList: (state, action) => {
            state.list = action.payload.map((item) => ({
                ...item,
                key: item?.id,
                storeKey: 'upgradesList',
            }));
        },
        setUpgradesList: (state, action) => {
            const { Items, LastEvaluatedKey, isReload } = action.payload;

            let i = -1;
            const _list =
                (Items || [])
                    .filter((item) => item !== null)
                    .map((item) => {
                        i += 1;
                        return {
                            ...item,
                            key: item?.id,
                            storeKey: 'upgradesList',
                            indexPrefix: 'upgradesList_',
                            index: i + state?.list?.length || 0,
                        };
                    })
                    ?.sort((a, b) => parseFloat(b.score) - parseFloat(a.score)) || [];

            if (!isReload) {
                state.list = state.list.concat(_list);
            } else {
                state.selectedListKey = null;
                state.selectedListIndex = -1;
                state.list = _list;
            }

            state.nextCursor = LastEvaluatedKey || null;
            state.isFetching = false;
            state.loadingState = 'idle';
            state.hasDoneFirstLoad = true;
        },
        setUpgradesFetchingTrue: (state, action) => {
            state.loadingState = action.payload.loadingState;
            state.isFetching = true;
            state.nextCursor = null;

            if (action?.payload?.isReload) {
                state.categories = [];
                state.list = [];
                state.nextCursor = null;
            }
        },
        resetUpgrades: (state) => {
            state.categories = [];
            state.list = [];
            state.nextCursor = null;
            state.hasDoneFirstLoad = false;
            state.items = [];
            state.cursor = null;
            state.total = 0;
            state.status = 'idle';
            state.binKey = null;
            state.deckCount = 0;
            state.source = null;
            state.note = null;
            state.isFetchingSource = false;
            state.sourceLoadingState = 'idle';
            state.hasDoneFirstSourceLoad = false;
            state.error = null;
        },

        // Source-based (new API) reducers.
        setSourceFetching: (state, action) => {
            const { loadingState, isReload } = action.payload || {};
            state.isFetchingSource = true;
            state.sourceLoadingState = loadingState || 'loading';
            state.error = null;
            if (isReload) {
                state.items = [];
                state.cursor = null;
                state.total = 0;
                state.note = null;
                state.hasDoneFirstSourceLoad = false;
            }
        },
        setSourceResults: (state, action) => {
            const { response, isReload } = action.payload || {};
            const rawItems = Array.isArray(response?.Items) ? response.Items : [];
            let i = -1;
            const hydrated = rawItems
                .filter((item) => item != null)
                .map((item) => {
                    i += 1;
                    return {
                        ...item,
                        key: item?.cardId || item?.comboId || item?.key || `${i}`,
                        storeKey: 'upgradesItems',
                        indexPrefix: 'upgradesItems_',
                        index: i + (isReload ? 0 : state.items.length),
                    };
                });

            if (isReload) {
                state.items = hydrated;
            } else {
                state.items = state.items.concat(hydrated);
            }

            state.cursor = response?.cursor ?? null;
            state.total = response?.Total ?? state.items.length;
            state.status = response?.status ?? 'ready';
            state.binKey = response?.binKey ?? null;
            state.deckCount = response?.deckCount ?? 0;
            state.source = response?.source ?? null;
            state.note = response?.note ?? null;
            state.isFetchingSource = false;
            state.sourceLoadingState = 'idle';
            state.hasDoneFirstSourceLoad = true;
            state.error = null;
        },
        setSourceError: (state, action) => {
            state.error = action.payload?.message || 'Request failed';
            state.isFetchingSource = false;
            state.sourceLoadingState = 'idle';
            state.hasDoneFirstSourceLoad = true;
        },
    },
});

export const {
    resetUpgrades,
    setUpgradesList,
    emptyUpgradesList,
    setUpgradesFetchingTrue,
    setSourceFetching,
    setSourceResults,
    setSourceError,
} = upgradesSlice.actions;

/**
 * Legacy format-wide category search. Hits `/upgrades?category=<enum>` and
 * writes into `state.upgrades.list`. Unchanged behavior — the "By Role"
 * mode still drives off this thunk.
 */
export const fetchUpgradesListByCategory =
    (id, category, allowReservedList, colorFilters = null, selectedTypes = [], cursor, isReload = false) =>
    (dispatch) => {
        cursor = cursor !== -1 ? cursor : null;
        dispatch(setUpgradesFetchingTrue({ loadingState: cursor ? `loadingMore` : `loading`, isReload }));

        if (isReload) {
            dispatch(emptyUpgradesList([]));
        }

        DynamoConnector.getDeckUpgrades(
            id,
            category,
            allowReservedList,
            colorFilters,
            selectedTypes,
            cursor,
            (results) => {
                dispatch(setUpgradesList({ ...results, isReload }));
            }
        );
    };

/**
 * Source-based commander-tuned fetch. Drives every non-legacy mode of the
 * Upgrade Finder (staples, synergy_*, manabase, archetype, category_role,
 * combos, top_synergies). Callers pass the full params object — see
 * `upgrades-api-additions.md` for the per-source param shape.
 *
 * `isReload` should be true on every filter change so pagination resets;
 * paginate by re-calling with the returned `cursor`.
 */
export const fetchUpgrades =
    (params = {}, isReload = true) =>
    (dispatch) => {
        dispatch(
            setSourceFetching({
                loadingState: params?.cursor ? 'loadingMore' : 'loading',
                isReload,
            })
        );

        DynamoConnector.getDeckUpgradesExtended(params, (response) => {
            if (response?.error) {
                dispatch(setSourceError({ message: response.error }));
                return;
            }
            dispatch(setSourceResults({ response, isReload }));
        });
    };

export default upgradesSlice.reducer;
